import { LocalScriptContentProvider } from "../../typings/local-script-content-provider";
import { CookieWriterElementsProcessorFactory } from "@commonRendering/types";

export abstract class BaseDynamicElementScriptRenderer {

    protected constructor(
        protected readonly version: string | null,
        protected readonly commonScriptId: string,
        protected readonly commonScriptSrc: string,
        protected readonly localScriptIdName: string,
        protected readonly scriptContentProvider: LocalScriptContentProvider
    ) {
    }

    protected get isEssentialScript(): boolean {
        return true;
    }

    // Pay attention: localScriptIdName must ends with -widget-scripts due to scripts migration problem PAGES-1753
    private get localScriptId(): string {
        return this.localScriptIdName + "-widget-scripts";
    }

    public renderCommon(doc: Document): void {
        const existing = doc.getElementById(this.commonScriptId);
        if (existing) {
            if (this.version && existing.dataset.version === this.version) {
                return;
            }

            existing.remove();
        }

        const script = doc.createElement("script");
        script.type = "text/javascript";
        script.id = this.commonScriptId;
        script.src = this.commonScriptSrc;

        if (this.version) {
            script.dataset.version = this.version;
        }

        this.setEssentialAttributeTo(script);
        script.defer = true;

        this.processScriptAccordingToGdprSettings(doc, script);

        doc.head.appendChild(script);
    }

    private processScriptAccordingToGdprSettings(doc: Document, script: HTMLElement): void {
        if (this.isEssentialScript) {
            return;
        }

        const processor = CookieWriterElementsProcessorFactory.createIfNecessary(doc, script);
        processor?.disableCookieWriters();
    }

    public cleanupCommon(doc: Document): void {
        const script = doc.getElementById(this.commonScriptId);
        script?.remove();
    }

    public renderLocal(element: HTMLElement): void {
        const script = this.createScriptElement(element);
        if (!script) {
            return;
        }

        const scriptContentProviderOptions = {
            elementId: element.id,
            commonScriptId: this.commonScriptId
        };
        script.textContent = this.scriptContentProvider?.getScriptTextContent(scriptContentProviderOptions);
        element.appendChild(script);
        element.dataset.localScriptStatus = "pending";
    }

    public createScriptElement(element: HTMLElement): HTMLScriptElement | null {
        if (!element.id) {
            return null;
        }

        const scriptId = this.getScriptId(element);

        const existingScript = this.getScriptById(element, scriptId);
        existingScript?.remove();

        const newScript = document.createElement("script");
        newScript.type = "text/javascript";
        newScript.id = this.getScriptId(element);
        this.setEssentialAttributeTo(newScript);
        return newScript;
    }

    private getScriptId(element: HTMLElement): string {
        return `${element.id}-${this.localScriptId}`;
    }

    private getScriptById(element: HTMLElement, id: string): HTMLElement | null {
        return element.querySelector(`script[id="${id}"]`);
    }

    private setEssentialAttributeTo(script: HTMLScriptElement): void {
        if (this.isEssentialScript) {
            script.setAttribute("data-essential", "");
        } else {
            script.removeAttribute("data-essential");
        }
    }

}
